import { Kushki } from '@kushki/js';
import { BinInfoResponse } from '@kushki/js/lib/types/bin_info_response';
import { ErrorResponse } from '@kushki/js/lib/types/error_response';
import API_URLS from '../../../../../constants/api-urls';
import { LATINA_COUNTRY_CODE } from '../../../../../constants/latina-country';
import axios from '../../../../../utils/axios';
import session from '../../../../../utils/session';
import serializeQueryParams from '../../../../../utils/url/serialize-query-params';
import isMobileAppWithDeeplinkRedirect from '../../../../../utils/is-mobile-app';
import { isTest } from '../../../payment.helpers';
import { CARD_TYPES, KushkiPaymentRequestType } from '../constants/kushki';
import { getMobilePathParams } from '../utils/kushki';
import { getZeroInDecimal } from '../../../../../utils/price/get-zero-in-decimal';
import { getUserOS, getUserSID } from '../../../../../services/quality-events/quality-events.helpers';

type BinInfoFunctionProps = { cardNumber: string; kushkiEnv: Kushki };

export const requestBinInfo = ({ cardNumber, kushkiEnv }: BinInfoFunctionProps) => {
    return new Promise((resolve) => {
        if (cardNumber && cardNumber.length > 6) {
            const binInfoCallback = (response: BinInfoResponse | ErrorResponse) => {
                resolve(response);
            };

            kushkiEnv.requestBinInfo(
                {
                    bin: cardNumber.substring(0, 6)
                },
                binInfoCallback
            );
        } else {
            resolve({});
        }
    });
};

export const getKushkiCardToken = async (props: KushkiPaymentRequestType) => {
    const {
        paymentOptionsData,
        kushkiFormData,
        invoiceFormData,
        withInvoice,
        price,
        user,
        currency
    } = props;
    const queryParams = serializeQueryParams();
    const region = paymentOptionsData?.userCountry;
    const finalPrice = getZeroInDecimal(Number(price.replace(',', '.')));
    const countryCodeFromQuery = queryParams?.region;
    const currencyCode = isTest() ? queryParams.currency : currency.code;
    const inMobileAppWithDeeplinkRedirect = isMobileAppWithDeeplinkRedirect() ? '&deepLinkRedirect=true' : '';

    const kushkiCheckout = new Kushki({
        inTestEnvironment: paymentOptionsData.kushkiEnv.inTestEnvironment,
        merchantId: paymentOptionsData.kushkiEnv.merchantId
    });
    const binInfo = (await requestBinInfo({
        cardNumber: kushkiFormData.number,
        kushkiEnv: kushkiCheckout
    })) as { cardType: CARD_TYPES };

    if (region === LATINA_COUNTRY_CODE.CL && binInfo.cardType === CARD_TYPES.DEBIT) {
        const kushkiCallBack = await axios.get(
            isTest()
                ? `${API_URLS.KUSHKI_TRANSFER_CALLBACK}${currencyCode}${`?countryCode=${countryCodeFromQuery}`}${
                      session.isLoggedIn()
                          ? '?paymentMethod=KushkiCard'
                          : `&email=${invoiceFormData.email}&paymentMethod=KushkiCard${inMobileAppWithDeeplinkRedirect}`
                  }`
                : `${API_URLS.KUSHKI_TRANSFER_CALLBACK}${currencyCode}${
                      session.isLoggedIn()
                          ? '?paymentMethod=KushkiCard'
                          : `?email=${invoiceFormData.email}&paymentMethod=KushkiCard${inMobileAppWithDeeplinkRedirect}`
                  }`
        );

        const cardTokenData = {
            currency: currencyCode as never,
            description: 'Pago de bienes en eduki.com',
            email: user?.email ?? invoiceFormData.email,
            returnUrl: kushkiCallBack.callbackUrl,
            totalAmount: finalPrice
        };

        return new Promise((resolve) =>
            kushkiCheckout.requestCardAsyncToken(
                //@ts-ignore
                cardTokenData,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                async (response: any) => {
                    if (response.token) {
                        const postBody = () => {
                            if (session.isLoggedIn()) {
                                return withInvoice
                                    ? { token: response.token, webPay: 1, ...invoiceFormData, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) }
                                    : { acceptAgb: invoiceFormData.acceptAgb, token: response.token, webPay: 1, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) };
                            }
                            const paymentFormData = withInvoice
                                ? invoiceFormData
                                : { acceptAgb: invoiceFormData.acceptAgb, email: invoiceFormData.email };

                            return { ...paymentFormData, token: response.token, webPay: 1, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) };
                        };

                        const data = await axios.post(
                            countryCodeFromQuery
                                ? `${
                                      API_URLS.PAYMENT_ATTEMPT
                                  }/KushkiCard?countryCode=${countryCodeFromQuery}&orderToken=${
                                      kushkiCallBack.orderToken
                                  }&currencyCode=${currencyCode}${getMobilePathParams()}`
                                : `${API_URLS.PAYMENT_ATTEMPT}/KushkiCard?orderToken=${
                                      kushkiCallBack.orderToken
                                  }&currencyCode=${currencyCode}${inMobileAppWithDeeplinkRedirect}${getMobilePathParams()}`,
                            postBody()
                        );

                        resolve(data);
                    } else {
                        console.error(
                            'Error: ',
                            response.error,
                            'Code: ',
                            response.code,
                            'Message: ',
                            response.message
                        );
                        resolve({ code: response.code, error: response.error, message: response.message });
                    }
                }
            )
        );
    }

    const cardTokenData = {
        amount:
            region === LATINA_COUNTRY_CODE.CO || region === LATINA_COUNTRY_CODE.CL
                ? finalPrice
                : Number(price.replace(',', '.')),
        card: {
            cvc: kushkiFormData.cvc,
            expiryMonth: kushkiFormData.postDate.split('/')[0].trim(),
            expiryYear: kushkiFormData.postDate.split('/')[1].trim(),
            name: kushkiFormData.firstName.concat(` ${kushkiFormData.lastName}`),
            number: kushkiFormData.number.replace(/\s/g, '')
        },
        currency: currencyCode as never,
        email: user?.email ?? invoiceFormData.email
    };

    return new Promise((resolve) =>
        kushkiCheckout.requestToken(
            //@ts-ignore
            cardTokenData,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            async (response: any) => {
                if (response.token) {
                    const postBody = () => {
                        if (session.isLoggedIn()) {
                            return withInvoice
                                ? { token: response.token, ...invoiceFormData, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) }
                                : { acceptAgb: invoiceFormData.acceptAgb, token: response.token, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) };
                        }
                        const paymentFormData = withInvoice
                            ? invoiceFormData
                            : { acceptAgb: invoiceFormData.acceptAgb, email: invoiceFormData.email };

                        return { ...paymentFormData, token: response.token, sid: getUserSID(), ...(getUserOS() && { os: getUserOS() }) };
                    };

                    const data = await axios.post(
                        countryCodeFromQuery
                            ? `${
                                  API_URLS.PAYMENT_ATTEMPT
                              }/KushkiCard?countryCode=${countryCodeFromQuery}&currencyCode=${currencyCode}${inMobileAppWithDeeplinkRedirect}${getMobilePathParams()}`
                            : `${
                                  API_URLS.PAYMENT_ATTEMPT
                              }/KushkiCard?currencyCode=${currencyCode}${inMobileAppWithDeeplinkRedirect}${getMobilePathParams()}`,
                        postBody()
                    );

                    resolve(data);
                } else {
                    console.error('Error: ', response.error, 'Code: ', response.code, 'Message: ', response.message);
                    resolve({ code: response.code, error: response.error, message: response.message });
                }
            }
        )
    );
};
